import {h, Fragment} from "preact"
import { usePagination, useActions, useSizeOptions } from "@nosto/preact"
import { defaultConfig, sizes } from "../config"
// import Icon from "./elements/Icon"

function PageLink({ onClick, href, className, ariaLabel, children, disabled }) {
    if (disabled) {
        return (
            <span
                class={`flex items-center ns-gap-x-0-125 text-t-brand-foreground-quaternary ns-cursor-not-allowed ${className}`}
                aria-label={ariaLabel}
                aria-disabled='true'
            >
                {children}
            </span>
        )
    }

    return (
        <a
            class={`flex items-center ns-gap-x-0-125 text-t-brand-foreground-secondary !no-underline transition-[color] duration-300 ease-out hover:text-t-brand-foreground ${className}`}
            href={href}
            aria-label={ariaLabel}
            onClick={onClick}
        >
            {children}
        </a>
    )
}

export default function Pagination() {
    const { prev, first, pages, last, next } = usePagination({
        width: 5
    })

    const { updateSearch, simulateUpdateSearch } = useActions()
    const { from, to, total } = useSizeOptions(sizes, defaultConfig.serpSize)

    function pageLinkProps({ from }) {
        return {
            onClick: e => {
                e.preventDefault()
                updateSearch({ products: { from } })
                scrollTo(0, 0)
            },
            href: simulateUpdateSearch({ products: { from } }).url
        }
    }

    const shownItems = total < to ? total : to
    const itemsText = `Showing ${shownItems} items / ${total} Items`

    return (
        <nav class='ns-pagination pt-xs px-md pb-xl lg:p-xl' role='navigation' aria-label='Pagination'>
            <div class='flex justify-between items-start lg:items-center'>
                {/* Previous Button - only display when there's a previous page */}
                <div class='flex items-center ns-h-10 ns-l-h-auto'>
                    {prev ? (
                        <PageLink
                            {...pageLinkProps(prev)}
                            ariaLabel="Previous page"
                        >
                            <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false" class="icon icon-arrow rotate-180" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11.75 14.5L17.25 9M17.25 9L11.75 3.5M17.25 9L0.750001 9" stroke="currentColor" stroke-width="0.75"/>
                            </svg>
                            <span class='hidden lg:block lg:body-l lg:leading-none'>
                                Prev
                            </span>
                        </PageLink>
                    ) : (
                        <div class="w-4 h-10"></div>
                    )}
                </div>

                {/* Page Numbers */}
                <ul class='flex items-center justify-center ns-gap-x-sm list-none' role='list'>
                    {first && (
                        <>
                            <li class='list-none'>
                                <a
                                    {...pageLinkProps(first)}
                                    class='body-l text-[0.75rem] min-w-5 ns-h-1-5625 flex justify-center items-center no-underline !outline-0 text-t-brand-foreground-secondary border-b-2 border-[transparent] transition-[color, border-color] duration-300 ease-out hover:no-underline hover:text-t-brand-foreground-secondary hover:border-t-brand-foreground-secondary focus:border-u-focus'
                                    aria-label={`Page ${first.page}`}
                                >
                                    {first.page}
                                </a>
                            </li>
                            <li class='list-none'>
                                <span class='body-l text-[0.75rem] min-w-5 ns-h-1-5625 flex justify-center items-center no-underline text-t-brand-foreground-secondary border-b-2 border-[transparent]'>
                                    ...
                                </span>
                            </li>
                        </>
                    )}

                    {pages.map(page => (
                        <li key={page.page} class='list-none'>
                            {page.current ? (
                                <a
                                    role='link'
                                    aria-disabled='true'
                                    class='body-l text-[0.75rem] min-w-5 ns-h-1-5625 flex justify-center items-center no-underline !outline-0 text-t-brand-foreground-secondary border-b-2 border-t-brand-foreground-secondary transition-[color, border-color] duration-300 ease-out hover:no-underline hover:text-t-brand-foreground hover:border-t-brand-foreground focus:border-u-focus'
                                    aria-current='page'
                                    aria-label={`Page ${page.page}`}
                                >
                                    {page.page}
                                </a>
                            ) : (
                                <a
                                    {...pageLinkProps(page)}
                                    class='body-l text-[0.75rem] min-w-5 ns-h-1-5625 flex justify-center items-center no-underline !outline-0 text-t-brand-foreground-secondary border-b-2 border-[transparent] transition-[color, border-color] duration-300 ease-out hover:no-underline hover:text-t-brand-foreground-secondary hover:border-t-brand-foreground-secondary focus:border-u-focus'
                                    aria-label={`Page ${page.page}`}
                                >
                                    {page.page}
                                </a>
                            )}
                        </li>
                    ))}

                    {last && (
                        <>
                            <li class='list-none'>
                                <span class='body-l text-[0.75rem] min-w-5 ns-h-1-5625 flex justify-center items-center no-underline text-t-brand-foreground-secondary border-b-2 border-[transparent]'>
                                    ...
                                </span>
                            </li>
                            <li class='list-none'>
                                <a
                                    {...pageLinkProps(last)}
                                    class='body-l text-[0.75rem] min-w-5 ns-h-1-5625 flex justify-center items-center no-underline !outline-0 text-t-brand-foreground-secondary border-b-2 border-[transparent] transition-[color, border-color] duration-300 ease-out hover:no-underline hover:text-t-brand-foreground-secondary hover:border-t-brand-foreground-secondary focus:border-u-focus'
                                    aria-label={`Page ${last.page}`}
                                >
                                    {last.page}
                                </a>
                            </li>
                        </>
                    )}
                </ul>

                {/* Next Button - only display when there's a next page */}
                <div class='flex items-center ns-h-10 ns-l-h-auto'>
                    {next ? (
                        <PageLink
                            {...pageLinkProps(next)}
                            ariaLabel="Next page"
                        >
                            <span class='hidden lg:block lg:body-l lg:leading-none'>
                                Next
                            </span>
                            <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false" class="icon icon-arrow" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11.75 14.5L17.25 9M17.25 9L11.75 3.5M17.25 9L0.750001 9" stroke="currentColor" stroke-width="0.75"/>
                            </svg>
                        </PageLink>
                    ) : (
                        <div class="w-4 h-10"></div>
                    )}
                </div>
            </div>

            {/* Items Counter */}
            <div class='ns-pagination-items text-center'>
                <span class='caption leading-none tracking-[0.03em] text-t-brand-foreground-secondary'>
                    {itemsText}
                </span>
            </div>
        </nav>
    )
}